import { tx, txt, useContent } from "@/content/ContentContext";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Pill, ShoppingBasket, Loader2, Check, MapPin, UtensilsCrossed } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getReferral } from "@/lib/referral";
import { toE164 } from "@/lib/phone";
import { CountryPicker, RegionSelect, taxOf, useCountries } from "@/components/CountryPicker";

const TYPES = () => ([
  { value: "general_retail", label: tx("signup.general_retail"), hint: tx("signup.groceries_household_convenience"), icon: ShoppingBasket },
  { value: "pharmacy", label: tx("signup.pharmacy_medical_store"), hint: tx("signup.batches_expiry_automatic_fefo"), icon: Pill },
  { value: "restaurant", label: tx("signup.restaurant"), hint: tx("signup.tables_kots_kitchen_view_billing"), icon: UtensilsCrossed },
]);

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const REF = /^[A-Z0-9][A-Z0-9_-]{1,39}$/;
const FIELDS = ["business_name", "business_type", "email", "password", "whatsapp", "country", "region", "referral"];

function FieldError({ field, errors }) {
  return errors[field] ? <div id={`err-${field}`} className="text-xs font-semibold text-rose-600" data-testid={`signup-error-${field}`}>{errors[field]}</div> : null;
}

function validate(v, { country, regionNeeded, regionWord, refState }) {
  const e = {};
  if (!v.business_name) e.business_name = tx("signup.enter_your_business_name");
  else if (v.business_name.length > 120) e.business_name = tx("signup.business_name_must_be_120");
  if (!v.business_type) e.business_type = tx("signup.choose_your_business_type");
  if (!v.email) e.email = tx("signup.enter_your_email_address");
  else if (!EMAIL.test(v.email)) e.email = tx("signup.enter_a_valid_email_address");
  if (!v.password) e.password = tx("signup.enter_a_password");
  else if (v.password.length < 6) e.password = tx("signup.password_must_be_at_least");
  else if (v.password.length > 128) e.password = tx("signup.password_must_be_128_characters");
  if (!country) e.country = tx("signup.select_your_country");
  if (v.whatsapp && !toE164(country, v.whatsapp)) e.whatsapp = tx("signup.whatsapp_invalid");
  if (regionNeeded) e.region = tx("signup.select_your_region", { region: regionWord });
  if (v.referral && !REF.test(v.referral)) e.referral = tx("signup.referral_codes_use_letters_numbers");
  else if (v.referral && refState === "bad") e.referral = tx("signup.referral_code_not_found");
  return e;
}

const tz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "";

function useReferral() {
  const [code, setCode] = useState(getReferral);
  const [state, setState] = useState(""); // "" | "ok" | "bad"
  const check = async (c = code) => {
    const v = c.trim().toUpperCase();
    if (!v) return setState("");
    try {
      await api.get(`/referral/${encodeURIComponent(v)}`);
      setState("ok");
    } catch {
      setState("bad");
    }
  };
  useEffect(() => { if (code) check(code); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return { code, setCode: (v) => { setCode(v); setState(""); }, state, check };
}

function LocationBlock({ countries, loc, setLoc, geo, errors, clear }) {
  const c = countries.find((x) => x.code === loc.country);
  const t = taxOf(c, loc.region);
  const needsPick = c?.regions && !loc.regionPicked;
  return (
    <div className="space-y-3 rounded-xl bg-slate-100 p-3" data-testid="signup-detected-country">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-600">
        <MapPin className="h-4 w-4 text-amber-600" /> {geo ? "Country (detected)" : tx("signup.detecting_your_country")}
      </div>
      <CountryPicker value={loc.country} countries={countries} onChange={(v) => { clear("country", "region"); setLoc({ country: v, region: countries.find((x) => x.code === v)?.regions?.[0]?.code || null, regionPicked: false }); }} testid="signup-country" />
      <FieldError field="country" errors={errors} />
      <RegionSelect country={c} value={loc.region} onChange={(v) => { clear("region"); setLoc({ ...loc, region: v, regionPicked: true }); }} testid="signup-region" />
      <FieldError field="region" errors={errors} />
      {needsPick && !errors.region && <div className="text-xs font-semibold text-amber-700" data-testid="signup-region-required">Please confirm your {c.code === "US" ? "state" : "province"}</div>}
      {t && (
        <div className="font-mono text-xs text-slate-600" data-testid="signup-tax-summary">
          {t.currency} · {t.decimals} decimals · {t.tax_label === "None" ? "No tax" : `${t.tax_label} ${t.tax_rate}%`} <span className="text-slate-400">{txt("signup.change_later_in_settings")}</span>
        </div>
      )}
    </div>
  );
}

export default function Signup() {
  useContent();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ business_type: "" }); // text inputs are uncontrolled so autofill values survive re-renders
  const [geo, setGeo] = useState(null);
  const [loc, setLoc] = useState({ country: "", region: null, regionPicked: false });
  const countries = useCountries();
  const ref = useReferral();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const form = useRef(null);

  useEffect(() => {
    api.get("/geo/detect", { params: { tz: tz(), locale: navigator.language } }).then((r) => {
      setGeo(r.data);
      setLoc((l) => (l.country ? l : { country: r.data.country, region: r.data.region || null, regionPicked: !!r.data.region_detected }));
    }).catch(() => setLoc((l) => (l.country ? l : { country: "OTHER", region: null, regionPicked: false })));
  }, []);

  const clear = (...ks) => setErrors((x) => (ks.some((k) => x[k]) ? Object.fromEntries(Object.entries(x).filter(([k]) => !ks.includes(k))) : x));
  const set = (k) => () => clear(k);
  const focusField = (k) => {
    const el = form.current?.querySelector({ business_type: '[data-testid^="signup-type-"]', country: '[data-testid="signup-country-picker"]', region: '[data-testid="signup-region-select"]', referral: "#ref" }[k] || `[name="${k}"]`);
    el?.focus();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    // read the real DOM values so browser autofill / password managers work even without onChange
    const fd = new FormData(e.currentTarget);
    const v = { business_name: String(fd.get("business_name") || "").trim(), email: String(fd.get("email") || "").trim(), password: String(fd.get("password") || ""),
                business_type: f.business_type, referral: String(fd.get("referral_code") || "").trim().toUpperCase(), whatsapp: String(fd.get("whatsapp") || "").trim() };
    if (v.referral !== ref.code.trim().toUpperCase()) ref.setCode(v.referral);
    const c = countries.find((x) => x.code === loc.country);
    const errs = validate(v, { country: loc.country, regionNeeded: !!c?.regions && !loc.regionPicked, regionWord: c?.code === "US" ? "state" : "province", refState: ref.state });
    setErrors(errs);
    setError("");
    const first = FIELDS.find((k) => errs[k]);
    if (first) return focusField(first);
    setBusy(true);
    try {
      const body = { business_name: v.business_name, business_type: v.business_type, email: v.email, password: v.password, timezone: tz(), locale: navigator.language,
                     country: loc.country, region: loc.region, referral_code: v.referral || null, whatsapp: v.whatsapp ? toE164(loc.country, v.whatsapp) : null };
      const { data } = await api.post("/auth/signup", body);
      signIn(data, { identifier: v.email, password: v.password });
      nav("/pos");
    } catch (err) {
      const msg = errMsg(err);
      const field = /email/i.test(msg) ? "email" : /password/i.test(msg) ? "password" : /business name/i.test(msg) ? "business_name" : /referral/i.test(msg) ? "referral" : null;
      if (field) { setErrors({ [field]: msg }); focusField(field); } else setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={tx("signup.open_your_register")} subtitle={tx("signup.four_fields_sample_products_are")}>
      <form ref={form} onSubmit={submit} noValidate className="space-y-5" data-testid="signup-form">
        <div className="space-y-2">
          <Label htmlFor="bn">{txt("signup.business_name")}</Label>
          <Input id="bn" name="business_name" autoComplete="organization" aria-invalid={!!errors.business_name} aria-describedby="err-business_name" className="h-12" defaultValue="" onChange={set("business_name")} placeholder={tx("signup.e_g_test_mart")} data-testid="signup-business-name-input" />
          <FieldError field="business_name" errors={errors} />
        </div>
        <div className="space-y-2">
          <Label>{txt("signup.business_type")}</Label>
          <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" data-testid="signup-business-type-group">
            {TYPES().map(({ value, label, hint, icon: Icon }) => {
              const on = f.business_type === value;
              return (
                <button type="button" role="radio" aria-checked={on} key={value} onClick={() => { clear("business_type"); setF((s) => ({ ...s, business_type: value })); }} data-testid={`signup-type-${value}`}
                  className={`relative flex min-h-[88px] flex-col items-start gap-1 rounded-xl border-2 p-3.5 text-left transition-[border-color,background-color,transform] active:scale-[0.98] ${on ? "border-slate-900 bg-amber-50" : "border-slate-200 bg-white hover:border-slate-400"}`}>
                  <Icon className={`h-5 w-5 ${on ? "text-amber-600" : "text-slate-500"}`} />
                  <span className="font-semibold text-slate-900">{label}</span>
                  <span className="text-xs text-slate-500">{hint}</span>
                  {on && <Check className="absolute right-3 top-3 h-4 w-4 text-slate-900" />}
                </button>
              );
            })}
          </div>
          <FieldError field="business_type" errors={errors} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="em">{txt("signup.owner_email")}</Label>
          <Input id="em" name="email" type="email" autoComplete="email" aria-invalid={!!errors.email} aria-describedby="err-email" className="h-12" defaultValue="" onChange={set("email")} placeholder={tx("signup.you_shop_com")} data-testid="signup-email-input" />
          <FieldError field="email" errors={errors} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pw">{txt("signup.password")}</Label>
          <Input id="pw" name="password" type="password" autoComplete="new-password" aria-invalid={!!errors.password} aria-describedby="err-password" className="h-12" defaultValue="" onChange={set("password")} placeholder={tx("signup.at_least_6_characters")} data-testid="signup-password-input" />
          <FieldError field="password" errors={errors} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="wa">{txt("signup.whatsapp_number")} <span className="font-normal text-slate-400">{txt("signup.optional")}</span></Label>
          <Input id="wa" name="whatsapp" type="tel" inputMode="tel" autoComplete="tel" aria-invalid={!!errors.whatsapp} aria-describedby="err-whatsapp" className="h-12" defaultValue="" onChange={set("whatsapp")} placeholder={tx("signup.whatsapp_placeholder")} data-testid="signup-whatsapp-input" />
          <FieldError field="whatsapp" errors={errors} />
        </div>
        <LocationBlock countries={countries} loc={loc} setLoc={setLoc} geo={geo} errors={errors} clear={clear} />
        <div className="space-y-2">
          <Label htmlFor="ref">{txt("signup.referral_code")} <span className="font-normal text-slate-400">{txt("signup.optional")}</span></Label>
          <Input id="ref" name="referral_code" autoComplete="off" aria-invalid={!!errors.referral} aria-describedby="err-referral" className="h-12 font-mono uppercase" defaultValue={ref.code} onChange={(e) => { clear("referral"); ref.setCode(e.target.value); }} onBlur={() => ref.check()} placeholder={tx("signup.e_g_smart20")} data-testid="signup-referral-input" />
          <FieldError field="referral" errors={errors} />
          {ref.state === "bad" && !errors.referral && <div className="text-xs font-semibold text-rose-600" data-testid="signup-referral-error">{txt("signup.referral_code_not_found")}</div>}
          {ref.state === "ok" && <div className="text-xs font-semibold text-emerald-700" data-testid="signup-referral-ok">{txt("signup.referral_code_applied")}</div>}
        </div>
        {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm font-medium text-rose-700" data-testid="signup-error">{error}</div>}
        <button type="submit" disabled={busy} data-testid="signup-submit-button" className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-900 font-semibold text-white transition-[background-color,transform] hover:bg-slate-800 active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-slate-300">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create account & open POS
        </button>
        <p className="text-center text-sm text-slate-500">
          {txt("signup.already_have_an_account")} <Link to="/login" className="font-semibold text-slate-900 underline decoration-amber-500 decoration-2 underline-offset-4" data-testid="go-to-login-link">{txt("signup.log_in")}</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
